import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { formattedDateTime } from '../../../../utils/dateUtils';
import { isDefined } from '../../../../utils/variables';
import { AssetManager } from '../../../../viewer/asset-manager';
import { Geometry } from '../../../../viewer/elements/geometry';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { disposeRecursive } from '../../../../viewer/utilities/three/dispose';
import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { useSubscribe } from '../../../hooks/use-subscribe';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';

type SaveAssetParams = {
  input: string;
  asset: Geometry;
  defaultName: string;
};

type MetaData = {
  title: string;
  value?: string;
};

async function saveAsset({ asset, input, defaultName }: SaveAssetParams) {
  asset.name = input.trim().length > 0 ? input : defaultName;
  asset.setDepthTest(true);
  AssetManager.instance.addAsset(asset);
  AssetManager.instance.select(asset);
  await AssetManager.instance.save(asset);
}

function useSaveAssetMutation(onSuccess: () => void) {
  const { buildErrorList, handleError } = useErrorHandling({ displayNonApiErrors: false });
  const mutation = useMutation({
    mutationFn: (params: SaveAssetParams) => saveAsset(params),
    retry: false,
    networkMode: 'always',
    onError: (error) => handleError(error),
    onSuccess,
  });

  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);

  return {
    saveAsset: mutation.mutateAsync,
    isPending: mutation.isPending,
    errorList,
  };
}

export const SaveGeometryAssetDialog = () => {
  const { t } = useTranslation();
  const [asset, setAsset] = useState<Geometry>();
  const [input, setInput] = useState('');
  const confirmRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialog = useDialog();

  const defaultName = useMemo(
    () =>
      isDefined(asset)
        ? `${
            asset.isArea
              ? t('area.defaultName', { ns: 'skyviewAssetMenu' })
              : t('line.defaultName', { ns: 'skyviewAssetMenu' })
          } ${formattedDateTime(Date.now())}`
        : undefined,
    [asset, t],
  );

  useSubscribe(SubscriptionTopic.ShowSaveGeometryAssetDialog, (event) => {
    setAsset(event.asset);
    setInput('');
    dialog.show(event.client);
  });

  useEffect(() => {
    // Clone the temporary asset so it doesn't conflict with the measure tool.
    const addedAsset = asset?.clone();

    if (isDefined(addedAsset)) {
      AssetManager.instance.addTemp(addedAsset);
    }

    return () => {
      if (addedAsset) {
        AssetManager.instance.removeTemp(addedAsset);
      }
    };
  }, [asset]);

  const onAssetSaved = useCallback(() => {
    setAsset(undefined);
    dialog.hide();
  }, [setAsset, dialog]);

  const { saveAsset, isPending, errorList } = useSaveAssetMutation(onAssetSaved);

  const metaData = useMemo(() => {
    const items: [MetaData, MetaData][] = [];

    if (asset?.isArea === true) {
      items.push([
        {
          title: t('area.section.surface3d', { ns: 'skyviewAssetMenu' }),
          value: asset.getArea()?.toFixed(2),
        },
        {
          title: t('area.section.surface2d', { ns: 'skyviewAssetMenu' }),
          value: asset.getPlaneArea()?.toFixed(2),
        },
      ]);
      items.push([
        {
          title: t('area.section.circumference', { ns: 'skyviewAssetMenu' }),
          value: asset.getDistance()?.toFixed(2),
        },
        {
          title: t('area.section.length2d', { ns: 'skyviewAssetMenu' }),
          value: asset.getPlaneDistance()?.toFixed(2),
        },
      ]);
    }

    if (asset?.isArea === false) {
      items.push([
        {
          title: t('line.section.length3d', { ns: 'skyviewAssetMenu' }),
          value: asset.getDistance()?.toFixed(2),
        },
        {
          title: t('line.section.length2d', { ns: 'skyviewAssetMenu' }),
          value: asset.getPlaneDistance()?.toFixed(2),
        },
      ]);
    }

    return items;
  }, [asset, t]);

  const onDiscard = useCallback(() => {
    if (!isPending) {
      setAsset((oldValue) => {
        if (oldValue) {
          disposeRecursive(oldValue);
        }

        return undefined;
      });
      dialog.hide();
    }
  }, [isPending, dialog]);

  return dialog.render((pos) => (
    <Dialog
      closeButtonRef={closeRef}
      closeIcon={true}
      confirmButtonRef={confirmRef}
      position={
        pos
          ? {
              top: pos.y,
              left: pos.x,
            }
          : 'center'
      }
      onClose={onDiscard}
    >
      {{
        header: t('geometryAssetDialog.title', { ns: 'skyview' }),
        content: (
          <Stack spacing={1}>
            <LabelledContainer text={t('name', { ns: 'common' })}>
              <TextBox
                autoFocus={true}
                disabled={isPending}
                placeholder={defaultName}
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
            </LabelledContainer>

            {metaData.map((x, idx) => (
              <Stack direction="row" key={idx}>
                <MetaDataLabel text={x[0].title}>
                  <span>{x[0].value}</span>
                </MetaDataLabel>
                <MetaDataLabel text={x[1].title}>
                  <span>{x[1].value}</span>
                </MetaDataLabel>
              </Stack>
            ))}

            {errorList}
          </Stack>
        ),
        footer: {
          right: (
            <>
              {asset && defaultName && (
                <Button
                  color="primary"
                  disabled={isPending}
                  ref={confirmRef}
                  variant="contained"
                  onClick={() =>
                    saveAsset({
                      asset,
                      defaultName,
                      input,
                    })
                  }
                >
                  {t('confirm', { ns: 'common' })}
                </Button>
              )}

              <Button disabled={isPending} ref={closeRef} variant="text" onClick={onDiscard}>
                {t('discard', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  ));
};

const MetaDataLabel = styled(LabelledContainer)`
  flex: 1;
`;
